import React from 'react';

import { useIntl } from 'react-intl-next';

import CalendarPlusIcon from '@atlaskit/icon/core/calendar-plus';
import FlagIcon from '@atlaskit/icon/core/flag';
import PremiumIcon from '@atlaskit/icon/core/premium';
import Image from '@atlaskit/image';
import { ConfluenceLogo } from '@atlaskit/logo';
import { Modal } from '@atlassian/growth-pattern-library-modal';

import ConfluenceStandardOfferCalendarModalRightSideSVG from '../../assets/svg/ConfluenceStandardOfferCalendarModalRightSide.svg';
import ConfluenceStandardOfferCalendarModalRightSideSVGDark from '../../assets/svg/ConfluenceStandardOfferCalendarModalRightSideDark.svg';
import { Product, recordSecondModalShown, type StorageAdapter } from '../../utils';
import { ScreenEvent, useAnalytics } from '../../utils/analytics';
import {
	type BusinessLogicAnalyticsAttributes,
	type CommonAnalyticsAttributes,
	CONFLUENCE_STANDARD_OFFER_CALENDAR_MODAL,
	type ModalDismissalMethod,
	type ModalShownPreOrPostUpgrade,
	type OfferShownType,
	type SupportedProduct,
} from '../../utils/types';
import { useModalState } from '../../utils/useModalState';
import { EditionHighlightsLeftContent } from '../EditionHighlightsLeftContent';
import { PrimaryAndSecondaryCTAActionsGroup } from '../PrimaryAndSecondaryCTAActionsGroup';
import { SideContentWithModalCloseButton } from '../SideContentWithModalCloseButton';

import { messages } from './messages';

export const StandardOfferCalendarModalRightSide = () => (
	<Image
		src={ConfluenceStandardOfferCalendarModalRightSideSVG}
		srcDark={ConfluenceStandardOfferCalendarModalRightSideSVGDark}
		alt="Calendars"
	/>
);

export const ConfluenceStandardOfferCalendarModal = ({
	analyticsAttributes,
	experienceUrl,
	storageAdapter,
}: {
	analyticsAttributes: BusinessLogicAnalyticsAttributes;
	experienceUrl: string;
	storageAdapter: StorageAdapter;
}) => {
	const intl = useIntl();

	const { isOpen, handleClose } = useModalState();

	const componentAttributes: CommonAnalyticsAttributes & BusinessLogicAnalyticsAttributes = {
		...analyticsAttributes,
		product: 'confluence' as SupportedProduct,
		offerShown: 'CALENDAR' as OfferShownType,
		modalShownPrePostUpgrade: 'PRE_UPGRADE' as ModalShownPreOrPostUpgrade,
	};

	const { clickedDismissModal, clickedPrimaryCTAOnModal } = useAnalytics();

	if (!isOpen) {
		return null;
	}

	const handleDismissModal = (dismissalMethod: ModalDismissalMethod) => {
		clickedDismissModal(CONFLUENCE_STANDARD_OFFER_CALENDAR_MODAL, {
			...componentAttributes,
			dismissalMethod,
		});
		recordSecondModalShown(storageAdapter, Product.Confluence);
		handleClose();
	};

	const handlePrimaryCTAClick = () => {
		clickedPrimaryCTAOnModal(CONFLUENCE_STANDARD_OFFER_CALENDAR_MODAL, componentAttributes);
		handleClose();
		recordSecondModalShown(storageAdapter, Product.Confluence);
	};

	return (
		<Modal
			height="560px"
			logo={<ConfluenceLogo shouldUseNewLogoDesign size="xsmall" />}
			title={intl.formatMessage(messages.standardOfferCalendarTitle)}
			subtitle={intl.formatMessage(messages.standardOfferCalendarSubtitle)}
			testId="growth-pattern-library-modal"
			onClose={() => {
				handleDismissModal('modalOverlayClick');
			}}
			sideContent={SideContentWithModalCloseButton(<StandardOfferCalendarModalRightSide />, () => {
				handleDismissModal('closeButton');
			})}
			actions={
				<PrimaryAndSecondaryCTAActionsGroup
					secondaryCTALabel={intl.formatMessage(messages.standardOfferCalendarSecondaryCTA)}
					onSecondaryCTAClick={() => {
						handleDismissModal('secondaryCTAButton');
					}}
					primaryCTALabel={intl.formatMessage(messages.standardOfferCalendarPrimaryCTA)}
					onPrimaryCTAClick={handlePrimaryCTAClick}
					href={experienceUrl}
					isLoading={false}
				/>
			}
		>
			<EditionHighlightsLeftContent
				highlights={[
					{
						icon: CalendarPlusIcon,
						title: intl.formatMessage(messages.calendarBenefitsTrack),
					},
					{
						icon: FlagIcon,
						title: intl.formatMessage(messages.calendarBenefitsSeeDeadlines),
					},
					{
						icon: PremiumIcon,
						title: intl.formatMessage(messages.calendarBenefitsLinkEvents),
					},
				]}
				disclaimerText={intl.formatMessage(messages.standardOfferCalendarDisclaimerText)}
			/>
			<ScreenEvent name="upgradeFreePoolTargetExperimentModal" attributes={componentAttributes} />
		</Modal>
	);
};
